import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  closeTopmostBackClosableLayer,
  closeTopmostModal,
  visibleBackClosableLayers,
  visibleModalLayers,
} from "@thuthi/ui/hooks/use-native-back-close-modals";
import subscriptionManager from "../app/SubscriptionManager";
import { disallowedTopic, topicUrl } from "../app/utils";
import routes from "./routes";
import connectionManager from "../app/ConnectionManager";
import poller from "../app/Poller";
import pruner from "../app/Pruner";
import session from "../app/Session";
import accountApi from "../app/AccountApi";
import versionChecker from "../app/VersionChecker";
import { UnauthorizedError } from "../app/errors";
import notifier from "../app/Notifier";
import { EVENT_MESSAGE_DELETE, EVENT_MESSAGE_CLEAR } from "../app/events";
import nativeBridge from "../app/NativeBridge";
import { backBridgeName } from "../app/NativeBridgeContract.generated";
import { subscribeTopic } from "./SubscribeDialog";
import type { Account, Notification, Subscription } from "../app/types";

/**
 * connectionManager(SSE 스트림 하나)와 subscriptionManager 를 잇는다. 구독 목록이 바뀌면 스트림을 다시 열고,
 * 받은 메시지·삭제·읽음 이벤트를 로컬 DB 에 반영한다. `sync` 이벤트(다른 기기에서 구독·설정 변경)는 계정을 다시 읽는다.
 */
export const useConnectionListeners = (subscriptions: Subscription[] | null | undefined) => {
  const subscriptionIds = JSON.stringify(subscriptions?.map(({ id }) => id));
  const stableSubscriptions = useMemo(() => subscriptions, [subscriptionIds]);

  useEffect(() => {
    const handleNotification = async (subscription: Subscription, notification: Notification) => {
      // This logic is (partially) duplicated in
      // - Android: SubscriberService::onNotificationReceived()
      // - Web app: Poller.poll()
      if (notification.event === EVENT_MESSAGE_DELETE && notification.sequence_id) {
        await subscriptionManager.deleteNotificationBySequenceId(subscription.id, notification.sequence_id);
        await notifier.cancel(subscription, notification);
      } else if (notification.event === EVENT_MESSAGE_CLEAR && notification.sequence_id) {
        await subscriptionManager.markNotificationReadBySequenceId(subscription.id, notification.sequence_id);
        await notifier.cancel(subscription, notification);
      } else {
        const added = await subscriptionManager.addOrReplaceNotification(subscription.id, notification);
        if (added) {
          await subscriptionManager.notify(subscription.id, notification);
        }
      }
    };

    const handleMessage = async (subscriptionId: string, message: Notification) => {
      const subscription = await subscriptionManager.get(subscriptionId);
      // Race condition: sometimes the subscription is already unsubscribed before the message is handled
      if (subscription && !subscription.internal) {
        await handleNotification(subscription, message);
      }
    };

    connectionManager.registerStateListener((id, state) => subscriptionManager.updateState(id, state));
    connectionManager.registerMessageListener(handleMessage);
    connectionManager.registerSyncListener(() => accountApi.sync());

    return () => {
      connectionManager.resetStateListener();
      connectionManager.resetMessageListener();
      connectionManager.resetSyncListener();
    };
  }, []);

  // When subscriptions change, refresh the stream
  useEffect(() => {
    connectionManager.refresh(stableSubscriptions);
  }, [stableSubscriptions]);
};

/**
 * Automatically adds a subscription if we navigate to a page that has not been subscribed to.
 * This will only be run once after the initial page load.
 */
export const useAutoSubscribe = (subscriptions: Subscription[] | null | undefined, selected: Subscription | null | undefined) => {
  const [hasRun, setHasRun] = useState(false);
  const params = useParams();

  useEffect(() => {
    const loaded = subscriptions !== null && subscriptions !== undefined;
    if (!loaded || hasRun) {
      return;
    }
    setHasRun(true);
    // /api/v2 는 이 서버만 다룬다 — 예전 /<다른서버>/<토픽> 경로는 자동 구독하지 않는다
    const eligible = params.topic && !params.baseUrl && !selected && !disallowedTopic(params.topic);
    if (eligible) {
      const baseUrl = config.base_url;
      console.log(`[Hooks] Auto-subscribing to ${topicUrl(baseUrl, params.topic!)}`);
      (async () => {
        try {
          const subscription = await subscribeTopic(baseUrl, params.topic!, {});
          poller.pollInBackground(subscription); // Dangle!
        } catch (e) {
          console.log(`[Hooks] Auto-subscribing failed`, e);
          if (e instanceof UnauthorizedError) {
            await session.resetAndRedirect(routes.login);
          }
        }
      })();
    }
  }, [params, subscriptions, selected, hasRun]);
};

/**
 * Hook to return a value that's refreshed when the notification permission changes
 */
export const useNotificationPermissionListener = <T>(query: () => T): T => {
  const [result, setResult] = useState(query());

  useEffect(() => {
    const handler = () => {
      setResult(query());
    };

    if ("permissions" in navigator) {
      navigator.permissions.query({ name: "notifications" }).then((permission) => {
        permission.addEventListener("change", handler);

        return () => {
          permission.removeEventListener("change", handler);
        };
      });
    }
  }, []);

  return result;
};

export const useNativeBridgeSync = (subscriptions: Subscription[] | null | undefined) => {
  const subscriptionSyncKey = JSON.stringify(
    subscriptions
      ?.filter((subscription) => !subscription.internal)
      .map(({ baseUrl, topic, displayName, mutedUntil, icon }) => ({ baseUrl, topic, displayName, mutedUntil, icon }))
      .sort((a, b) => `${a.baseUrl}/${a.topic}`.localeCompare(`${b.baseUrl}/${b.topic}`))
  );

  useEffect(() => {
    if (!nativeBridge.isNativeApp() || !subscriptions) {
      return;
    }
    nativeBridge.syncSubscriptions(subscriptions);
  }, [subscriptionSyncKey]);
};

/** The object the Android app calls on hardware back (see CLAUDE.md "뒤로가기 팝업 닫기"). */
export interface NativeBackBridge {
  goBack: () => boolean;
}

// backBridgeName is a generated constant, so the bridge is attached via a computed key.
const backBridgeWindow = window as unknown as Record<string, NativeBackBridge | undefined>;

export const useNativeBackButtonBridge = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const stackRef = useRef<string[]>([]);

  useEffect(() => {
    if (!nativeBridge.isNativeApp()) {
      return undefined;
    }

    const current = `${location.pathname}${location.search}${location.hash}`;
    const currentPath = location.pathname || routes.app;
    const isRoot = currentPath === routes.app || currentPath === "/" || currentPath === "";
    
    if (isRoot) {
      stackRef.current = [current];
    } else {
      const stack = stackRef.current;
      if (stack[stack.length - 2] === current) {
        stack.pop();
      } else if (stack[stack.length - 1] !== current) {
        stack.push(current);
      }
    }

    backBridgeWindow[backBridgeName] = {
      goBack: () => {
        if (closeTopmostBackClosableLayer()) {
          return true;
        }

        if (closeTopmostModal()) {
          return true;
        }

        const path = window.location.pathname || routes.app;
        const isRoot = path === routes.app || path === "/" || path === "";
        if (routes.isTopLevel(path)) {
          return false; // 맨 위 화면(모든 알림·구독) — 앱이 ntfy 를 닫고 홈으로
        }
        if (stackRef.current.length > 1) {
          navigate(-1);
          return true;
        }
        if (!isRoot) {
          navigate(routes.app, { replace: true });
          return true;
        }
        return false;
      },
    };

    return () => {
      delete backBridgeWindow[backBridgeName];
    };
  }, [location.hash, location.pathname, location.search, navigate]);
};

export const useNativePullRefreshState = () => {
  const location = useLocation();
  const enabledRef = useRef<boolean | null>(null);

  useEffect(() => {
    if (!nativeBridge.isNativeApp()) {
      return undefined;
    }

    const main = document.getElementById("main");
    if (!main) {
      return undefined;
    }

    const update = () => {
      const enabled = main.scrollTop <= 0 && visibleModalLayers().length === 0 && visibleBackClosableLayers().length === 0;
      if (enabledRef.current === enabled) {
        return;
      }
      enabledRef.current = enabled;
      nativeBridge.setPullRefreshEnabled(enabled);
    };
    const scheduleUpdate = () => window.requestAnimationFrame(update);
    const observer = new MutationObserver(scheduleUpdate);

    main.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);
    observer.observe(document.body, { attributes: true, childList: true, subtree: true });
    update();

    return () => {
      main.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
      observer.disconnect();
      enabledRef.current = null;
      nativeBridge.setPullRefreshEnabled(false);
    };
  }, [location.hash, location.pathname, location.search]);
};

/**
 * Start the poller and the pruner. This is done in a side effect as opposed to just in Pruner.js
 * and Poller.js, because side effect imports are not a thing in JS, and "Optimize imports" cleans
 * up "unused" imports. See https://github.com/binwiederhier/ntfy/issues/186.
 */

const startWorkers = () => {
  poller.startWorker();
  pruner.startWorker();
  versionChecker.startWorker();
};

const stopWorkers = () => {
  poller.stopWorker();
  pruner.stopWorker();
  versionChecker.stopWorker();
};

export const useBackgroundProcesses = () => {
  useEffect(() => {
    console.log("[useBackgroundProcesses] mounting");
    startWorkers();

    return () => {
      console.log("[useBackgroundProcesses] unloading");
      stopWorkers();
    };
  }, []);
};

export const useAccountListener = (setAccount: (account: Account) => void) => {
  useEffect(() => {
    accountApi.registerListener(setAccount);
    accountApi.sync(); // Dangle
    return () => {
      accountApi.resetListener();
    };
  }, []);
};

/**
 * Hook to detect version/config changes and call the provided callback when a change is detected.
 */
export const useVersionChangeListener = (onVersionChange: () => void) => {
  useEffect(() => {
    versionChecker.registerListener(onVersionChange);
    return () => {
      versionChecker.resetListener();
    };
  }, [onVersionChange]);
};
